import { useState } from 'react';
import { AthenaCore } from 'athena-core';
import {
  dispatch,
  OpenModalSchema,
  RedirectSchema,
  ReportPulseSchema,
} from '../../athena-core/src/athena-core-bus';
import { User } from '../types/Auth';
import Consultation from 'src/components/Dashboard/Consultation';

// Booking details for create function
interface BookingDetails {
  selectedDate: Date;
  startTime: string;
  endTime: string;
  timeZone: string;
  phone?: string;
}

// Consultation details for get function to retrieve consultation.
export interface ConsultationDetails {
  consultation_id: number;
  startTime: string;
  endTime: string;
  resolutionStatus: string;
  selectedDate: string;
  hasRescheduled: boolean;
  userId: number;
  isPaid: boolean;
  chargeAmount: number;
}

export interface Consultations extends Array<ConsultationDetails> {}

interface UseBookConsultationResult {
  fetchConsultation: () => Promise<ConsultationDetails | null>;
  bookConsultation: (details: BookingDetails) => Promise<void>;
  fetchConsultationsByClient: (user: User) => Promise<Consultations | null>;
  consultation: ConsultationDetails | null;
  consultations: Consultations | null;
  loading: boolean;
}

export const useConsultation = (): UseBookConsultationResult => {
  const [loading, setLoading] = useState(false);
  const [consultation, setConsultation] = useState<ConsultationDetails | null>(
    null
  );

  const [consultations, setConsultations] = useState<Consultations | null>(
    null
  );

  const fetchConsultation = async (): Promise<ConsultationDetails | null> => {
    setLoading(true);

    try {
      const response = await fetch(
        `${
          import.meta.env.VITE_API_URL
        }/api/consultation/my-active-consultation`,
        {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      const data = await response.json();
      console.log(data);
      console.log(response);
      console.log('Consultation fetched:', data.consultation);
      // console.log(response);

      if (!response.ok) {
        // await dispatch({
        //   status: response.status,
        //   kind: 'ERROR',
        //   title: 'Error',
        //   message: data?.message || 'failed to retrieve booking consultaiton',
        // });
        AthenaCore.throwError({
          status: response.status,
          message: data?.message || 'failed to retrieve booking consultation',
        });
      }

      if (data.consultation) {
        setConsultation(data.consultation);
      }

      setLoading(false);
      return data.consultation;
    } catch (error: unknown) {
      setLoading(false);
      await dispatch(
        {
          status: 500,
          kind: 'ERROR',
          title: 'Error',
          message:
            error instanceof Error ? error.message : 'Internal Server Error.',
        },
        {
          requireApproval: false,
        }
      );
      // AthenaCore.throwError({
      //   status: 500,
      //   message:
      //     error instanceof Error ? error.message : 'Internal Server Error.',
      // });

      return null;
    }
  };

  const fetchConsultationsByClient = async (
    user: User
  ): Promise<Consultations | null> => {
    setLoading(true);
    try {
      const response = await fetch(
        `${
          import.meta.env.VITE_API_URL
        }/api/consultation/all-consultations-by-consultant?client_id=${
          user.user_id
        }`,
        {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      console.log('Fetch consultations by consultant response:', response);

      if (!response.ok) {
        AthenaCore.throwError({
          status: response.status,
          message: 'Failed to fetch consultations.',
        });
      }

      const data = await response.json();
      setLoading(false);
      setConsultations(data.consultations);
      return data.consultations;
    } catch (error: unknown) {
      setLoading(false);
      await dispatch(
        {
          status: 500,
          kind: 'ERROR',
          title: 'Error',
          message:
            error instanceof Error ? error.message : 'Internal Server Error.',
        },
        {
          requireApproval: false,
        }
      );
      // AthenaCore.throwError({
      //   status: 500,
      //   message:
      //     error instanceof Error ? error.message : 'Internal Server Error.',
      // });

      return null;
    }
  };

  const bookConsultation = async (details: BookingDetails): Promise<void> => {
    setLoading(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/consultation/create`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(details),
        }
      );

      if (!response.ok) {
        // AthenaCore.throwError({
        //   status: response.status,
        //   message: 'Failed to book consultation. Please try again.',
        // });

        await dispatch(
          {
            status: response.status,
            kind: 'ERROR',
            title: 'Error',
            message: 'Failed to book consultation. Please try again.',
          },
          {
            requireApproval: false,
          }
        );
      }

      const data = await response.json();
      await dispatch(
        {
          kind: 'OPEN_MODAL',
          title: 'Success',
          message: 'Consultation successfully booked.',
          type: 'success',
        },
        { requireApproval: false }
      );
      // AthenaCore.openModal({
      //   title: 'Success',
      //   message: 'Consultation successfully booked.',
      //   type: 'success',
      // });
      AthenaCore.sealLog('Consultation booked', { details, data });

      // AthenaCore.redirect('/payment?type=consultation');

      await dispatch({
        kind: 'REDIRECT',
        path: '/payment?type=consultation',
      });
    } catch (error) {
      // AthenaCore.openModal({
      //   title: 'Booking Error',
      //   message:
      //     error instanceof Error ? error.message : 'Unknown booking error.',
      //   type: 'error',
      // });

      await dispatch(
        {
          status: 500,
          kind: 'ERROR',
          title: 'Error',
          message: error instanceof Error ? error.message : 'Booking error.',
        },
        {
          requireApproval: false,
        }
      );

      // AthenaCore.throwError({
      //   status: 500,
      //   message: error instanceof Error ? error.message : 'Booking error.',
      // });
    } finally {
      setLoading(false);
    }
  };

  return {
    fetchConsultation,
    fetchConsultationsByClient,
    bookConsultation,
    consultation,
    consultations,
    loading,
  };
};
